import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/**
 * Shared across every instance on the page (drawer + cart page never render
 * at the same time, but this guards against any future duplicate mount, and
 * against overlapping add/remove calls while one is already in flight).
 */
let reconciling = false;

/**
 * Asks Shopify's cart API to also return freshly rendered HTML for every cart
 * section on the page, so the cart list can morph straight from the response
 * rather than making its own follow-up section fetch.
 * @returns {{sections?: string, sections_url?: string}}
 */
function sectionsParams() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  if (ids.size === 0) return {};
  return { sections: Array.from(ids).join(','), sections_url: window.location.pathname };
}

/**
 * Plays the cart's own row-removal collapse on the row for a cart line key,
 * resolving once it finishes (or immediately if there's no row / reduced motion).
 * @param {string} key
 * @returns {Promise<void>}
 */
function collapseCartRow(key) {
  const row = document.querySelector(`.cart-items__table-row[data-key="${CSS.escape(key)}"]`);
  if (!(row instanceof HTMLElement) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return Promise.resolve();
  }

  row.style.setProperty('--row-height', `${row.clientHeight}px`);
  row.classList.add('removing');

  return new Promise((resolve) => {
    row.addEventListener('animationend', () => resolve(), { once: true });
    setTimeout(resolve, 700);
  });
}

/**
 * @typedef {object} Tier
 * @property {number} variantId
 * @property {number} thresholdCents
 * @property {string} title
 * @property {string} [shortTitle]
 */

/**
 * @typedef {object} Marker
 * @property {number} thresholdCents
 * @property {string} label
 * @property {string} [shortLabel]
 */

/**
 * @typedef {object} Milestone A tier or marker, normalized to a common
 *   shape for bar/marker rendering and progress messaging. Only entries
 *   with a variantId are actual gifts that get added/removed from the cart
 *   - a plain milestone (e.g. "$150 -> Free Shipping") is purely visual.
 *   "shortTitle" is what's shown directly on the bar (kept brief so labels
 *   don't crowd each other); "title" is the full name shown in the
 *   hover/tap tooltip.
 * @property {number} thresholdCents
 * @property {string} title
 * @property {string} shortTitle
 * @property {number} [variantId]
 */

/**
 * Reads free-gift-with-purchase tier configuration published by the
 * "free_gift_tiers" section (in header-group.json) as a JSON script tag.
 * That section renders on every page (the header renders everywhere), so
 * this tag is always present, regardless of where this component itself is
 * mounted (cart drawer, cart page).
 *
 * @returns {{progressText: string, allUnlockedText: string, barTopPaddingPx: number, tiers: Tier[], markers: Marker[]} | null}
 */
function readConfig() {
  const configEl = document.getElementById('free-gift-tiers-config');
  if (!configEl?.textContent) return null;

  try {
    const parsed = JSON.parse(configEl.textContent);
    const tiers = Array.isArray(parsed.tiers) ? parsed.tiers : [];
    const markers = Array.isArray(parsed.markers) ? parsed.markers : [];
    if (tiers.length === 0 && markers.length === 0) return null;

    return {
      progressText: parsed.progressText || '',
      allUnlockedText: parsed.allUnlockedText || '',
      barTopPaddingPx: Number(parsed.barTopPaddingPx) || 0,
      tiers: tiers.slice().sort((a, b) => a.thresholdCents - b.thresholdCents),
      markers: markers.slice().sort((a, b) => a.thresholdCents - b.thresholdCents),
    };
  } catch {
    return null;
  }
}

/**
 * Shows progress toward one or more free-gift-with-purchase spend tiers
 * plus any purely-visual spend milestones (e.g. a free shipping threshold),
 * and automatically adds/removes each tier's gift product as the cart
 * crosses that tier's threshold. Once a gift is in the cart, the customer
 * can change its variant (e.g. colour) the same way as any other cart line,
 * via cart-item-variant-picker.js.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [barFill]
 * @property {HTMLElement} [markersContainer]
 * @property {HTMLElement} [message]
 *
 * @extends {Component<Refs>}
 */
class FreeGiftProgress extends Component {
  /** @type {{progressText: string, allUnlockedText: string, tiers: Tier[], markers: Marker[]} | null} */
  #config = null;

  /** @type {{items: Array<{variant_id: number, properties?: Record<string,string>, line_price: number, key: string}>, items_subtotal_price: number} | null} */
  #lastCart = null;

  /** @type {MutationObserver | undefined} */
  #resetObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#config = readConfig();
    if (!this.#config) return;

    this.style.setProperty('--free-gift-progress-bar-top-padding', `${this.#config.barTopPaddingPx}px`);

    this.#buildMarkers(this.#milestones());
    this.removeAttribute('hidden');

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#fetchCartAndReconcile();
    this.#watchForExternalReset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#resetObserver?.disconnect();
  }

  /**
   * Gift tiers and plain milestones combined into one threshold-sorted list
   * for bar/marker rendering and progress messaging. Reconciliation (adding
   * removing gifts) always uses this.#config.tiers directly instead, since
   * milestones are purely visual and never trigger a cart action.
   * @returns {Milestone[]}
   */
  #milestones() {
    if (!this.#config) return [];
    const tierMilestones = this.#config.tiers.map((tier) => ({
      thresholdCents: tier.thresholdCents,
      title: tier.title,
      shortTitle: tier.shortTitle || tier.title,
      variantId: tier.variantId,
    }));
    const plainMilestones = this.#config.markers.map((marker) => ({
      thresholdCents: marker.thresholdCents,
      title: marker.label,
      shortTitle: marker.shortLabel || marker.label,
    }));
    return [...tierMilestones, ...plainMilestones].sort((a, b) => a.thresholdCents - b.thresholdCents);
  }

  /**
   * Other cart components on the page (e.g. the cart line items list) fall
   * back to re-fetching and morphing their entire containing section
   * whenever a cart-update event doesn't carry pre-rendered section HTML -
   * including the events this component itself dispatches after adding a
   * gift. That morph reverts this element back to its plain server-
   * rendered shell - which strips anything applied purely via JS and never
   * present in the server HTML: the "hidden" removal, the marker content,
   * and the --free-gift-progress-bar-top-padding inline style (the actual
   * cause of the top-padding setting appearing to work in the editor, which
   * re-renders less aggressively, but not on the live site, where this
   * reset happens almost immediately after mount). This detects that reset
   * and immediately reapplies everything from preserved state instead of
   * waiting for the next cart event.
   */
  #watchForExternalReset() {
    const { markersContainer } = this.refs;
    if (!markersContainer) return;

    this.#resetObserver = new MutationObserver(() => {
      if (!this.#config) return;

      if (this.hasAttribute('hidden')) {
        this.removeAttribute('hidden');
      }

      if (this.style.getPropertyValue('--free-gift-progress-bar-top-padding') !== `${this.#config.barTopPaddingPx}px`) {
        this.style.setProperty('--free-gift-progress-bar-top-padding', `${this.#config.barTopPaddingPx}px`);
      }

      if (markersContainer.children.length > 0) return;

      const milestones = this.#milestones();
      if (milestones.length === 0) return;

      this.#buildMarkers(milestones);

      if (this.#lastCart) {
        this.#render(milestones, this.#adjustedSubtotal(this.#lastCart));
      }
    });

    this.#resetObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'style'] });
  }

  /** @param {Milestone[]} milestones */
  #buildMarkers(milestones) {
    const { markersContainer } = this.refs;
    if (!markersContainer) return;

    const maxThreshold = milestones.at(-1)?.thresholdCents ?? 0;
    if (maxThreshold <= 0) return;

    markersContainer.replaceChildren(
      ...milestones.map((milestone, index) => {
        const marker = document.createElement('div');
        marker.className = 'free-gift-progress__marker';
        marker.style.left = `${Math.min(100, (milestone.thresholdCents / maxThreshold) * 100)}%`;
        marker.dataset.index = String(index);

        // A real <button> so touch/keyboard users can tap or focus to open
        // the tooltip - :hover alone (used for mouse users via CSS) doesn't
        // work on touch devices.
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'free-gift-progress__marker-trigger';
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('click', () => this.#toggleTooltip(marker));

        const dot = document.createElement('span');
        dot.className = 'free-gift-progress__marker-dot';
        dot.innerHTML =
          '<svg class="free-gift-progress__marker-check" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12.5L9.5 18L20 6" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>';

        const shortLabel = document.createElement('span');
        shortLabel.className = 'free-gift-progress__marker-short-label';
        shortLabel.textContent = milestone.shortTitle;

        trigger.append(dot, shortLabel);

        const tooltip = document.createElement('div');
        tooltip.className = 'free-gift-progress__marker-tooltip';
        tooltip.setAttribute('role', 'tooltip');
        tooltip.textContent = milestone.title;

        marker.append(trigger, tooltip);
        return marker;
      })
    );
  }

  /** @param {HTMLElement} marker */
  #toggleTooltip(marker) {
    const isOpen = marker.classList.contains('free-gift-progress__marker--active');

    this.refs.markersContainer?.querySelectorAll('.free-gift-progress__marker--active').forEach((openMarker) => {
      openMarker.classList.remove('free-gift-progress__marker--active');
      openMarker.querySelector('.free-gift-progress__marker-trigger')?.setAttribute('aria-expanded', 'false');
    });

    if (!isOpen) {
      marker.classList.add('free-gift-progress__marker--active');
      marker.querySelector('.free-gift-progress__marker-trigger')?.setAttribute('aria-expanded', 'true');
    }
  }

  #handleCartUpdate = (event) => {
    const cart = event.detail?.resource;
    if (!cart || !Array.isArray(cart.items)) return;
    this.#reconcile(cart);
  };

  #fetchCartAndReconcile() {
    fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)
      .then((response) => response.json())
      .then((cart) => this.#reconcile(cart))
      .catch((error) => console.error('[free-gift-progress]', error));
  }

  /**
   * @param {NonNullable<typeof this.#lastCart>} cart
   * @returns {number}
   */
  #giftLinesTotal(cart) {
    return cart.items
      .filter((item) => item.properties?._free_gift === 'true')
      .reduce((sum, item) => sum + item.line_price, 0);
  }

  /** @param {NonNullable<typeof this.#lastCart>} cart */
  #adjustedSubtotal(cart) {
    return (cart.items_subtotal_price ?? 0) - this.#giftLinesTotal(cart);
  }

  /**
   * A tier's gift can end up under a different variant than its configured
   * default once the customer changes it via the cart line variant picker
   * (cart-item-variant-picker.js), which preserves line properties as-is
   * through the swap. Each gift line is tagged with "_free_gift_tier" set
   * to the tier's stable default-variant id, so the tier it belongs to can
   * still be identified after a swap, regardless of which variant it's on.
   * @param {NonNullable<typeof this.#lastCart>} cart
   * @param {Tier} tier
   */
  #findGiftLineForTier(cart, tier) {
    return cart.items.find(
      (item) => item.properties?._free_gift === 'true' && item.properties?._free_gift_tier === String(tier.variantId)
    );
  }

  /** @param {NonNullable<typeof this.#lastCart>} cart */
  #reconcile(cart) {
    if (!this.#config) return;
    this.#lastCart = cart;
    const { tiers } = this.#config;

    const adjustedSubtotal = this.#adjustedSubtotal(cart);
    this.#render(this.#milestones(), adjustedSubtotal);

    if (reconciling) return;

    // Only act on one tier per pass - the resulting cart update re-triggers
    // #reconcile, which will then pick up the next tier that needs action.
    // This avoids overlapping fetch calls mutating the cart concurrently.
    // Plain milestones (no variantId) never reach this loop at all - see
    // this.#config.tiers vs this.#milestones().
    for (const tier of tiers) {
      const giftLine = this.#findGiftLineForTier(cart, tier);

      if (adjustedSubtotal >= tier.thresholdCents && !giftLine) {
        this.#addGift(tier);
        return;
      }

      if (giftLine && adjustedSubtotal < tier.thresholdCents) {
        this.#removeGift(giftLine);
        return;
      }
    }
  }

  /**
   * @param {Milestone[]} milestones
   * @param {number} adjustedSubtotal
   */
  #render(milestones, adjustedSubtotal) {
    if (!this.#config) return;

    const maxThreshold = milestones.at(-1)?.thresholdCents ?? 0;
    const { barFill, message, markersContainer } = this.refs;
    const percent = maxThreshold > 0 ? Math.min(100, Math.max(0, (adjustedSubtotal / maxThreshold) * 100)) : 0;

    if (barFill) barFill.style.width = `${percent}%`;

    const markerEls = markersContainer ? Array.from(markersContainer.children) : [];
    markerEls.forEach((marker) => {
      if (!(marker instanceof HTMLElement)) return;
      const milestone = milestones[Number(marker.dataset.index)];
      if (!milestone) return;
      marker.classList.toggle('free-gift-progress__marker--unlocked', adjustedSubtotal >= milestone.thresholdCents);
    });

    const nextLocked = milestones.find((milestone) => adjustedSubtotal < milestone.thresholdCents);
    const allUnlocked = !nextLocked;

    this.classList.toggle('free-gift-progress--unlocked', allUnlocked);

    if (!message) return;

    if (allUnlocked) {
      message.textContent = this.#config.allUnlockedText;
      return;
    }

    const remaining = Math.max(0, nextLocked.thresholdCents - adjustedSubtotal);
    const formatted = formatMoney(remaining, this.dataset.moneyFormat, this.dataset.currency);
    message.textContent = this.#config.progressText.replaceAll('[amount]', formatted).replaceAll('[product]', nextLocked.title);
  }

  /** @param {Tier} tier */
  #addGift(tier) {
    reconciling = true;

    const body = JSON.stringify({
      items: [
        {
          id: tier.variantId,
          quantity: 1,
          properties: { _free_gift: 'true', _free_gift_tier: String(tier.variantId) },
        },
      ],
      ...sectionsParams(),
    });

    // The add response already carries freshly rendered cart section HTML
    // (see sectionsParams), so the cart list morphs straight from that
    // instead of making a second, slow full-section fetch afterwards.
    // cart.js is only needed for the cart object other listeners read.
    fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((result) => {
        if (result.status) throw new Error(result.description || 'Failed to add free gift');
        return fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)
          .then((response) => response.json())
          .then((cart) => this.#dispatchCartUpdate(cart, result.sections));
      })
      .catch((error) => console.error('[free-gift-progress]', error))
      .finally(() => {
        reconciling = false;
      });
  }

  /**
   * @param {{key: string}} giftLine
   */
  #removeGift(giftLine) {
    reconciling = true;

    const body = JSON.stringify({ id: giftLine.key, quantity: 0, ...sectionsParams() });

    // Collapse the row first (same animation as removing an item by hand)
    // so it doesn't just vanish when the refreshed cart HTML lands.
    Promise.all([
      fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body })).then((response) => response.json()),
      collapseCartRow(giftLine.key),
    ])
      .then(([cart]) => {
        if (cart.status) throw new Error(cart.description || 'Failed to remove free gift');
        this.#dispatchCartUpdate(cart, cart.sections);
      })
      .catch((error) => console.error('[free-gift-progress]', error))
      .finally(() => {
        reconciling = false;
      });
  }

  /**
   * @param {{item_count: number}} cart
   * @param {Record<string, string> | undefined} sections
   */
  #dispatchCartUpdate(cart, sections) {
    document.dispatchEvent(
      new CartUpdateEvent(cart, this.id || 'free-gift-progress', {
        source: 'free-gift-progress',
        // The header cart bubble reads this - without it the count reset to 0.
        itemCount: cart.item_count,
        sections,
      })
    );
  }
}

if (!customElements.get('free-gift-progress')) {
  customElements.define('free-gift-progress', FreeGiftProgress);
}
